import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Buildings,
  EnvelopeSimple,
  Phone,
  PaperPlaneRight,
  Paperclip,
  X,
  CheckCircle,
  Sparkle,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import LegalConsent from '../components/LegalConsent';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ALLOWED = '.pdf,.doc,.docx,.xls,.xlsx,.csv,.png,.jpg,.jpeg,.webp,.heic';
const MAX_FILES = 10;
const MAX_SIZE = 25 * 1024 * 1024;

const ServiceRequestPage = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: '',
    organization: '',
    email: '',
    phone: '',
    service_type: '',
    notes: '',
  });
  const [files, setFiles] = useState([]);
  const [consent, setConsent] = useState(false);
  const [consentError, setConsentError] = useState('');

  useEffect(() => {
    fetch(`${API_URL}/api/services/catalogue`)
      .then((r) => r.json())
      .then((d) => setServices(d.services || []))
      .catch(() => toast.error('تعذّر تحميل قائمة الخدمات'));
  }, []);

  const addFiles = (newList) => {
    const arr = Array.from(newList);
    const next = [...files];
    for (const f of arr) {
      if (next.length >= MAX_FILES) {
        toast.error(`الحد الأقصى ${MAX_FILES} ملفات`);
        break;
      }
      if (f.size > MAX_SIZE) {
        toast.error(`${f.name}: يتجاوز الحد المسموح (25MB)`);
        continue;
      }
      next.push(f);
    }
    setFiles(next);
  };

  const removeFile = (idx) => setFiles(files.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.service_type) {
      toast.error('اختاري نوع الخدمة');
      return;
    }
    if (!consent) {
      setConsentError('يجب الموافقة على سياسة الخصوصية والشروط والأحكام قبل الإرسال');
      toast.error('يرجى الموافقة على السياسات قبل الإرسال');
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      files.forEach((f) => fd.append('files', f));
      const res = await fetch(`${API_URL}/api/service-requests`, {
        method: 'POST',
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'حدث خطأ');
      }
      const data = await res.json();
      navigate(`/services/request/success?token=${data.access_token}`);
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'حدث خطأ في إرسال الطلب');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main
      data-testid="service-request-page"
      className="pt-24 pb-16 min-h-screen bg-[#F7F5F2]"
      dir="rtl"
    >
      <section className="py-12 bg-[#F5F2EC]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <span className="inline-block px-4 py-2 rounded-full border border-[#D8D2C8] text-[#5D6E55] text-sm mb-4">
            طلب خدمة استشارية
          </span>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#1F3D2B] mb-4 font-['IBM_Plex_Sans_Arabic']">
            احكي لنا عن منشأتك
          </h1>
          <p className="text-[#6B7565] max-w-2xl mx-auto leading-8">
            اختاري الخدمة المناسبة، عبّي البيانات وارفعي ملفاتك، ونكمّل معك الخطوات بكل وضوح
          </p>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-3xl mx-auto px-4">
          <form
            onSubmit={submit}
            className="bg-white rounded-[24px] shadow-sm border border-[#E6E0D6] p-6 sm:p-8"
          >
            {/* Service selection */}
            <h2 className="text-lg font-bold text-[#1F3D2B] mb-3 font-['IBM_Plex_Sans_Arabic']">
              اختاري الخدمة
            </h2>
            <div className="grid sm:grid-cols-2 gap-3 mb-8">
              {services.map((s) => {
                const active = form.service_type === s.id;
                return (
                  <label
                    key={s.id}
                    data-testid={`service-option-${s.id}`}
                    className={`relative cursor-pointer p-4 rounded-2xl border-2 transition-all ${
                      active
                        ? 'border-[#4A641F] bg-[#F5F2EC]'
                        : 'border-[#E6E0D6] hover:border-[#D8D2C8] bg-white'
                    }`}
                  >
                    <input
                      type="radio"
                      name="service_type"
                      value={s.id}
                      checked={active}
                      onChange={() => setForm({ ...form, service_type: s.id })}
                      className="sr-only"
                    />
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 shrink-0 rounded-xl bg-[#1F3D2B] text-white text-sm font-bold flex items-center justify-center">
                        {s.stage}
                      </div>
                      <div className="flex-1">
                        <p className="font-bold text-[#1F3D2B] leading-tight">
                          {s.label_ar}
                        </p>
                        <p className="text-xs text-[#92A082] mt-0.5">{s.label_en}</p>
                        <p className="text-[11.5px] text-[#5D6E55] mt-2 leading-6">
                          يُحدَّد السعر بعد دراسة الاحتياج ونطاق العمل وإعداد العرض المالي.
                        </p>
                      </div>
                      {active && (
                        <CheckCircle
                          size={20}
                          weight="fill"
                          className="text-[#4A641F] shrink-0"
                        />
                      )}
                    </div>
                  </label>
                );
              })}
            </div>

            {/* Personal info */}
            <h2 className="text-lg font-bold text-[#1F3D2B] mb-3 font-['IBM_Plex_Sans_Arabic']">
              بيانات التواصل
            </h2>
            <div className="grid sm:grid-cols-2 gap-4 mb-5">
              <div>
                <Label htmlFor="name">الاسم الكامل *</Label>
                <Input
                  id="name"
                  data-testid="request-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  className="mt-1 rounded-xl"
                />
              </div>
              <div>
                <Label htmlFor="organization" className="flex items-center gap-1.5">
                  <Buildings size={14} />
                  اسم المنشأة *
                </Label>
                <Input
                  id="organization"
                  data-testid="request-org"
                  value={form.organization}
                  onChange={(e) =>
                    setForm({ ...form, organization: e.target.value })
                  }
                  required
                  className="mt-1 rounded-xl"
                />
              </div>
              <div>
                <Label htmlFor="email" className="flex items-center gap-1.5">
                  <EnvelopeSimple size={14} />
                  البريد الإلكتروني *
                </Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="request-email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  dir="ltr"
                  className="mt-1 rounded-xl text-right"
                />
              </div>
              <div>
                <Label htmlFor="phone" className="flex items-center gap-1.5">
                  <Phone size={14} />
                  رقم الجوال *
                </Label>
                <Input
                  id="phone"
                  data-testid="request-phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  required
                  dir="ltr"
                  className="mt-1 rounded-xl text-right"
                  placeholder="05xxxxxxxx"
                />
              </div>
            </div>

            {/* Notes */}
            <div className="mb-6">
              <Label htmlFor="notes">ملاحظات إضافية (اختياري)</Label>
              <Textarea
                id="notes"
                data-testid="request-notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={4}
                className="mt-1 rounded-xl"
                placeholder="اكتبي أي تفاصيل تساعدنا في فهم أولوياتك"
              />
            </div>

            {/* File upload */}
            <h2 className="text-lg font-bold text-[#1F3D2B] mb-3 font-['IBM_Plex_Sans_Arabic']">
              المرفقات (اختياري)
            </h2>
            <label
              htmlFor="files"
              className="block border-2 border-dashed border-[#D8D2C8] rounded-2xl p-6 text-center cursor-pointer hover:border-[#4A641F] transition-colors mb-4"
            >
              <Paperclip size={28} className="mx-auto text-[#7A866E] mb-2" />
              <p className="text-sm text-[#1F3D2B] font-medium">
                اضغطي لاختيار الملفات أو اسحبيها هنا
              </p>
              <p className="text-xs text-[#7A866E] mt-1">
                Word, PDF, Excel, صور — حد أقصى {MAX_FILES} ملفات / 25MB لكل ملف
              </p>
              <input
                id="files"
                type="file"
                data-testid="request-files-input"
                multiple
                accept={ALLOWED}
                className="sr-only"
                onChange={(e) => addFiles(e.target.files)}
              />
            </label>
            {files.length > 0 && (
              <ul className="space-y-2 mb-6">
                {files.map((f, idx) => (
                  <li
                    key={idx}
                    className="flex items-center justify-between bg-[#F5F2EC] rounded-xl p-3"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <Paperclip size={16} className="text-[#4A641F] shrink-0" />
                      <span className="text-sm text-[#1F3D2B] truncate">
                        {f.name}
                      </span>
                      <span className="text-xs text-[#7A866E] shrink-0">
                        ({Math.round(f.size / 1024)} KB)
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(idx)}
                      className="text-[#7A866E] hover:text-red-500 shrink-0"
                    >
                      <X size={18} />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <LegalConsent
              checked={consent}
              onChange={(v) => { setConsent(v); if (v) setConsentError(''); }}
              error={consentError}
              testId="sr-legal-consent"
            />

            <Button
              type="submit"
              data-testid="submit-request-btn"
              disabled={loading}
              className="w-full bg-[#1F3D2B] hover:bg-[#163020] text-white rounded-xl py-4 text-base font-bold flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  جاري الإرسال...
                </>
              ) : (
                <>
                  <PaperPlaneRight size={20} />
                  إرسال الطلب
                </>
              )}
            </Button>

            <p className="text-xs text-[#92A082] text-center mt-4 leading-7">
              <Sparkle size={12} weight="fill" className="inline text-[#4A641F]" />
              {' '}
              لن تُحاسبي الآن — سنراجع الطلب ونتواصل معك بخطوتنا التالية
            </p>
          </form>
        </div>
      </section>
    </main>
  );
};

export default ServiceRequestPage;
